// Phase 260 — useSportConfig hook
//
// Fetches and caches sport-config from /api/sports/registry. Result is keyed
// by sport (lowercase). Cache is module-scoped so multiple components viewing
// the same sport on the same page share a single network call.
//
// Falls back to a baked-in football config when the sport is unknown or the
// API errors. Cache hits resolve synchronously at render time (no state
// transition needed) so common navigations feel instant.
import { useEffect, useState } from 'react';
import api from '@/lib/api';

const FOOTBALL_FALLBACK = {
  key: 'football',
  display_name: 'Football',
  emoji: '⚽',
  surface_label: 'Pitch',
  surface_kind: 'pitch',
  boundary_label: 'Penalty Area / Touchline',
  primary_metric: { key: 'goals', label: 'Goals' },
  secondary_metric: { key: 'assists', label: 'Assists' },
  metric_fields: [
    { key: 'goals', label: 'Goals', unit: '' },
    { key: 'assists', label: 'Assists', unit: '' },
  ],
  positions: ['Striker', 'Midfielder', 'Defender', 'Goalkeeper', 'Winger'],
  team_lineup_supported: true,
  highlight_type: 'goal',
  color_accent: '#22c55e',
  default_heatmap_zones: [
    { x: 240, y: 60, intensity: 0.8, r: 30 },
    { x: 260, y: 100, intensity: 0.9, r: 35 },
    { x: 220, y: 130, intensity: 0.6, r: 25 },
    { x: 200, y: 80, intensity: 0.5, r: 20 },
    { x: 180, y: 100, intensity: 0.3, r: 15 },
    { x: 270, y: 50, intensity: 0.7, r: 28 },
  ],
  coaching_drills: {
    primary: ['1v1 finishing in the box', 'Volley conversion sequence', 'Penalty shooting routine'],
    secondary: ['Crossing & cutback combos', 'Through-ball precision drill', 'Set-piece delivery reps'],
    positioning: ['Pressing trigger shadows', 'Defensive shape shuttles', '1v1 jockey & contain'],
    precision: ['Passing rondos (4v1)', 'First-touch under pressure', 'Long-ball accuracy'],
    simulation: ['Full-pitch match simulation', 'Small-sided game (4v4)', 'Tactical scrimmage'],
  },
};

const cache = new Map();
const inFlight = new Map();

export function useSportConfig(rawSport) {
  const normalized = (rawSport || '').toLowerCase().trim() || 'football';
  // Synchronously resolve cache at render. `useState` lazy initializer means
  // the cache lookup runs once per remount; the effect handles key changes.
  const [version, setVersion] = useState(0);  // force re-read when fetch lands
  const cached = cache.get(normalized);

  useEffect(() => {
    if (cache.has(normalized)) return;  // already resolved — no-op
    let cancelled = false;

    const promise = inFlight.get(normalized) || (async () => {
      try {
        const res = await api.get(`/sports/registry/${normalized}`);
        cache.set(normalized, res.data);
        return res.data;
      } catch {
        // Unknown sport or network error — cache the fallback so we don't
        // re-fetch on every render. Caller still gets a usable config.
        cache.set(normalized, FOOTBALL_FALLBACK);
        return FOOTBALL_FALLBACK;
      } finally {
        inFlight.delete(normalized);
      }
    })();
    inFlight.set(normalized, promise);

    promise.then(() => {
      if (!cancelled) setVersion((v) => v + 1);
    });
    return () => { cancelled = true; };
  }, [normalized]);

  return {
    config: cached || FOOTBALL_FALLBACK,
    loading: !cached,
    _version: version,  // exposed for diagnostics; consumers can ignore
  };
}
